import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, ChevronRight, AlertCircle, KeyRound } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import EducationFAQ from "@/components/EducationFAQ";
import { useI18n } from "@/context/I18nContext";
import { langDisplayName } from "@/lib/languages";

export default function MyClasses() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [classes, setClasses] = useState(null);
  const [code, setCode] = useState("");
  useEffect(() => { api.get("/my-classes").then((r) => setClasses(r.data.classes || [])).catch(() => setClasses([])); }, []);

  const submit = (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (c.length === 6) navigate(`/join?code=${c}`);
  };

  return (
    <AppLayout>
      <Seo title={`${t("nav.myclasses")} · FluentLoop`} noindex />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl flex items-center gap-2"><GraduationCap className="w-7 h-7" /> {t("nav.myclasses")}</h1>
        <p className="text-gray-600 text-sm mt-1">{t("edu.stu.intro")}</p>

        {classes?.some((c) => c.pilot_expired) && <StudentExpiredNotice t={t} />}

        <div className="space-y-3 mt-6" data-testid="my-classes-list">
          {classes === null && <div className="nb-card h-24 animate-pulse" />}
          {classes?.length === 0 && <p className="text-sm text-gray-500" data-testid="my-classes-empty">{t("edu.stu.none")}</p>}
          {classes?.map((c) => (
            <Link key={c.class_id} to={`/my-classes/${c.class_id}`} className="nb-card p-4 flex items-center justify-between gap-3 hover:-translate-y-0.5 transition-transform" data-testid={`my-class-${c.class_id}`}>
              <div className="min-w-0">
                <div className="font-cabinet font-bold text-lg truncate" dir="auto">{c.name}</div>
                <div className="text-xs text-gray-600"><bdi>{c.organization?.name}</bdi> · {langDisplayName(c.target_language, locale)} · {c.level}{c.teacher_name ? <> · <bdi>{c.teacher_name}</bdi></> : null}</div>
                <div className="flex gap-2 mt-2 text-xs">
                  <span className="nb-tag bg-butter" data-testid={`my-class-unfinished-${c.class_id}`}>{t("edu.stu.unfinished", { n: c.assignments_unfinished })}</span>
                  <span className="nb-tag bg-mint" data-testid={`my-class-completed-${c.class_id}`}>{t("edu.stu.completedN", { n: c.assignments_completed })}</span>
                  {c.pilot_expired && <span className="nb-tag bg-peach">{t("edu.stu.pilotEndedTag")}</span>}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 shrink-0 rtl:rotate-180" />
            </Link>
          ))}
        </div>

        <form onSubmit={submit} className="nb-card p-4 mt-6 flex flex-wrap items-end gap-2" data-testid="join-code-form">
          <label className="flex-1 min-w-[12rem]">
            <span className="text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500 flex items-center gap-1"><KeyRound className="w-3 h-3" /> {t("edu.stu.haveCode")}</span>
            <input className="nb-input mt-1 uppercase tracking-widest" dir="ltr" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABC123" data-testid="join-code-input" />
          </label>
          <button className="nb-btn-primary py-2 px-4" data-testid="join-code-submit">{t("edu.stu.continue")}</button>
        </form>

        <div className="mt-10">
          <h2 className="font-cabinet font-extrabold text-2xl mb-3">{t("edu.faq.title")}</h2>
          <EducationFAQ groups={["students", "privacy"]} testid="student-faq" />
        </div>
      </div>
    </AppLayout>
  );
}

export function StudentExpiredNotice({ t }) {
  return (
    <div className="nb-card bg-peach p-4 mt-6 flex gap-3" data-testid="student-pilot-ended">
      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
      <p className="text-sm">{t("edu.stu.pilotEnded")}</p>
    </div>
  );
}
